// Backend
import React from 'react';
import axios from '../../helpers/AxiosInstance';

// Frontend
import { Grid, Cell } from 'react-foundation';

// Functionality
import { Link, useNavigate } from 'react-router-dom';

// Components
import {
  Header,
  DluchApi,
  FlashMessage,
  Flashable,
  enableFormButton,
  disableFormButton,
  ProgressBar,
  setToken,
} from '../../constants/SharedComponents';
import useCurrentUser from '../../hooks/useCurrentUser';

// Styles
import '../../stylesheets/components/forms/Conversations.css';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';

const ConversationsNew = () => {
  // API
  const currentUser = useCurrentUser();
  const redirectTo = '/conversations';
  const navigate = useNavigate();
  const token = setToken(currentUser);
  const headerParams = { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 };

  // Objects
  const [loading, setLoading] = React.useState(false);
  const [flashMessageObject, setFlashMessageObject] = React.useState({});
  const [inputs, setInputs] = React.useState({});
  const [data, setData] = React.useState(null);
  const [userInvitationPending, setUserInvitationPending] = React.useState(false);
  const [userInvitationSent, setUserInvitationSent] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [selected, setSelected] = React.useState({});
  const [zeroElements, setZeroElements] = React.useState(false);
  const [badQuery, setBadQuery] = React.useState(null);
  const basicFormButtons = React.useRef([]);

  // Navigation state management
  const [navigationHistory, setNavigationHistory] = React.useState(['initial']);

  // Functions
  function createConversation(formInputs) {
    let Api = new DluchApi('ConversationsNew');

    let requestPayload;

    if (data.id) {
      requestPayload = {
        attendeeId: data.id,
        conversationDescription: formInputs.content,
        isOrganiserManager: formInputs.role === 'employer',
      };
    }

    axios
      .post(Api.ApiRequestUrl(), requestPayload, headerParams)
      .then((response) => {
        let conversation = response.data.content.conversation;
        navigate(redirectTo + '/' + conversation.id, {
          state: Flashable(
            'You conversation has been created, the recipient has been notified, and you can now both start your prep.',
            'notice',
          ),
        });
      })
      .catch((error) => {
        if (error.response && error.response.data) {
          setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
        } else {
          setFlashMessageObject(Flashable(error.message, 'alert', false));
        }

        enableFormButton(basicFormButtons);
        setLoading(false);
      });
  }

  function findUser(formInputs) {
    let Api = new DluchApi('UsersShow');

    let getHeaderParams = {
      params: { email: formInputs.emailAddress },
      headers: { Authorization: `Bearer ${token}`, timeout: 30000 },
    };

    axios
      .get(Api.ApiRequestUrl(), getHeaderParams)
      .then((response) => {
        if (response.data.content.user.id === currentUser.userId) {
          setFlashMessageObject(Flashable('You cannot invite yourself.', 'alert', false));
          setUserInvitationPending(false);
          setUserInvitationSent(false);
        } else {
          setData({
            forename: response.data.content.user.firstname,
            surname: response.data.content.user.lastname,
            id: response.data.content.user.id,
            email_address: response.data.content.user.emailAddress,
          });
          setUserInvitationPending(false);
          setUserInvitationSent(false);
          setNavigationHistory(prev => [...prev, 'create']);
        }
      })
      .catch((error) => {
        if (error.response?.data?.errorDesc === "User email is not valid for your organisation's policy") {
          setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
          setUserInvitationPending(false);
          setUserInvitationSent(false);
        } else {
          setUserInvitationPending(true);
          setUserInvitationSent(false);
        }
      });
  }

  const handleSearch = (query) => {
    setLoading(true);

    let Api = new DluchApi('UsersQuery');

    let getHeaderParams = {
      params: { email: query },
      headers: { Authorization: `Bearer ${token}`, timeout: 30000 },
    };

    axios
      .get(Api.ApiRequestUrl(), getHeaderParams)
      .then((response) => {
        let filteredUsers = response.data.content.users;

        if (filteredUsers) {
          filteredUsers = response.data.content.users.filter(
            user => user.id !== currentUser.userId
          );

          setOptions(filteredUsers);
        }
        else {
          setOptions([]);
        }

        if (filteredUsers.length === 0) {
          setZeroElements(true);
          setBadQuery(query);
        }
        else {
          setZeroElements(false);
          setBadQuery(null);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const handleSelected = (option) => {
    if (option[0] && option[0].emailAddress && option[0].isActive) {
      setFlashMessageObject({});
      setSelected(option[0]);
      submitFindUser(option[0]);
    }
    else if (option[0] && !option[0].isActive) {
      setFlashMessageObject(Flashable("This user has not completed registration. They will need to complete registration before you can request a conversation with them.", 'alert', false));
    }
  }

  const handleSetInviteUserPage = () => {
    setInputs({
      email_address: badQuery
    });
    setUserInvitationPending(true);
    setUserInvitationSent(false);
    setNavigationHistory(prev => [...prev, 'invite']);
  }

  function inviteUser(formInputs) {
    let Api = new DluchApi('UsersNew');

    let requestPayload = { email: formInputs.email_address };

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (emailRegex.test(formInputs.email_address)) {
      axios
        .post(Api.ApiRequestUrl(), requestPayload, headerParams)
        .then((response) => {
          setFlashMessageObject(Flashable(response.data.content.description, 'notice', false));
          setUserInvitationPending(false);
          setUserInvitationSent(true);
          setNavigationHistory(prev => [...prev, 'invited']);
        })
        .catch(() => {
          findUser({
            emailAddress: formInputs.email_address
          });
        });
    }
    else {
      setFlashMessageObject(Flashable("Please enter a valid email address", 'alert', false));
    }
  }

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    disableFormButton(basicFormButtons);
    createConversation(inputs);
  };

  const findUserSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    findUser(selected);
    setLoading(false);
  };

  const submitFindUser = (formInput) => {
    setLoading(true);
    findUser(formInput);
    setLoading(false);
  };

  const inviteUserSubmit = React.useCallback((event) => {
    event.preventDefault();
    setLoading(true);
    inviteUser(inputs);
    setLoading(false);
  });

  const handleBackNavigation = () => {
    if (navigationHistory.length <= 1) {
      // If we're at the initial state or only have one item, go back to conversations
      navigate('/conversations');
      return;
    }

    // Remove current state and go to previous
    const newHistory = [...navigationHistory];
    newHistory.pop(); // Remove current state
    const previousState = newHistory[newHistory.length - 1];

    setNavigationHistory(newHistory);

    // Always reset to initial invite page
    switch (previousState) {
      case 'initial':
        setData(null);
        setUserInvitationPending(false);
        setUserInvitationSent(false);
        setSelected({});
        setOptions([]);
        setZeroElements(false);
        setBadQuery(null);
        break;
      case 'invite':
        setData(null);
        setUserInvitationPending(true);
        setUserInvitationSent(false);
        break;
      case 'invited':
        setUserInvitationSent(true);
        setUserInvitationPending(false);
        setData(null);
        break;
      default:
        navigate('/conversations');
    }
  };

  const filterBy = () => true;

  // Content
  return (
    <>
      {/* Custom styling for green radio buttons */}
      <style>
        {`
          .basic-form__input[type="radio"] {
            appearance: none;
            width: 16px;
            height: 16px;
            border: 2px solid #ccc;
            border-radius: 50%;
            background-color: white;
            position: relative;
            cursor: pointer;
            outline: none !important;
            transition: all 0.2s ease;
            margin-right: 8px;
            vertical-align: middle;
            flex-shrink: 0;
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }
          
          .basic-form__input[type="radio"]:checked {
            border-color: var(--color--brand-secondary);
            background-color: white;
            outline: none !important;
          }
          
          .basic-form__input[type="radio"]:checked::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: var(--color--brand-secondary);
          }
          
          .basic-form__input[type="radio"]:focus {
            outline: none !important;
            box-shadow: 0 0 0 2px rgba(80, 176, 100, 0.3);
          }
          
          .basic-form__input[type="radio"]:focus:not(:focus-visible) {
            box-shadow: none !important;
          }
          
          .basic-form__input[type="radio"]:hover:not(:checked) {
            border-color: var(--color--brand-secondary);
          }
          
          .basic-form__field--inline {
            display: flex !important;
            align-items: center !important;
            gap: 0 !important;
            flex-wrap: nowrap !important;
            margin-bottom: 0 !important;
          }
          
          .basic-form__field--inline .basic-form__label {
            margin: 0 !important;
            white-space: nowrap !important;
            cursor: pointer;
            margin-left: 8px !important;
            line-height: 1 !important;
            display: flex !important;
            align-items: center !important;
          }
          
          /* Container for radio button group */
          .radio-group-container {
            display: flex !important;
            gap: 25px !important;
            align-items: center !important;
            flex-wrap: nowrap !important;
            margin-bottom: 15px !important;
          }
        `}
      </style>

      <ProgressBar loading={loading} />

      <Header />

      <div className="grid-container">
        <Grid className="grid-padding-x align-justify">
          <Cell small={12}>
            <FlashMessage object={flashMessageObject} columnWrap={false} />

            {!data && !userInvitationPending && !userInvitationSent && (
              <div className="page-content">
                <div className="page-content__header">
                  <Link
                    to="/conversations"
                    className="page-content__button--medium"
                    id="page-navigation-button--go-back"
                  >
                    Back to Dashboard
                  </Link>
                </div>

                <h1 className="page-content__title">
                  Who would you like to have a conversation with?
                </h1>

                <p>
                  Start by entering an email address to search for a user.
                  If they are registered they will appear in a list to be selected from.
                  If that user is not currently registered and does not appear in the list they
                  can be invited by clicking the 'Invite User' button.
                </p>

                <form className="basic-form" onSubmit={findUserSubmit}>
                  <div className="basic-form__field">
                    <label htmlFor="email_address" className="basic-form__label">
                      Name:
                    </label>

                    <AsyncTypeahead
                      filterBy={filterBy}
                      id="email_search"
                      isLoading={loading}
                      minLength={3}
                      labelKey={option => `${option.emailAddress}`}
                      onSearch={handleSearch}
                      options={options}
                      onChange={handleSelected}
                      placeholder="Search for a team member within your organisation..."
                      renderMenuItemChildren={(option) => (
                        <>
                          {option.isActive && (
                            <span>{option.emailAddress} - {option.firstname} {option.lastname}</span>
                          )}
                          {!option.isActive && (
                            <span>{option.emailAddress} - {option.firstname} {option.lastname} - Incomplete Registration</span>
                          )}
                        </>


                      )}
                      emptyLabel="No matches found. Please invite this user using the 'Invite User' button below ↓"
                    />
                  </div>

                  {zeroElements && (
                    <div className="basic-form__actions button-set">
                      <input
                        value="Invite User"
                        className="basic-form__button"
                        id="page-form-button--complete"
                        onClick={handleSetInviteUserPage}
                        style={{ marginTop: 40 }}
                      />
                    </div>
                  )}
                </form>
              </div>
            )}

            {userInvitationPending && !userInvitationSent && (<div className="page-content">
              <div className="page-content__header">
                <button
                  onClick={handleBackNavigation}
                  className="page-content__button--medium page-content__button--back"
                  id="page-navigation-button--go-back"
                >
                  Go back
                </button>
              </div>

              <h1 className="page-content__title">Invite User</h1>

              <p>
                Enter the email of the user you wish to invite.
              </p>

              <input
                className="basic-form__input"
                type="email"
                name="email_address"
                id="email_address"
                value={inputs.email_address || ''}
                onChange={handleChange}
                required={true}
                placeholder="Email address (example@email.com)"
                pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
              />
              <p>
                This user isn't registered on the system, click the button below to invite them
              </p>
              <p>
                Please follow your organisations communication policy when sending an invite.
              </p>

              <p>
                <button
                  onClick={inviteUserSubmit}
                  className="page-content__button--medium"
                  id="page-form-button--submit"
                >
                  Invite user
                </button>
              </p>
            </div>
            )}            {userInvitationSent && (
              <div className="page-content">
                <h1 className="page-content__title">Invite Sent</h1>

                <Link
                  to="/conversations"
                  className="page-content__button--medium page-content__button--back"
                  id="page-navigation-button--go-back"
                >
                  Go back
                </Link>
              </div>
            )}

            {data && (
              <div className="page-content">                <div className="page-content__header">
                <button
                  onClick={handleBackNavigation}
                  className="page-content__button--medium page-content__button--back"
                  id="page-navigation-button--go-back"
                >
                  Go back
                </button>
              </div>

                <h1 className="page-content__title">Create Conversation</h1>
                <p>
                  Complete the form below to create a conversation with {data.forename}{' '}
                  {data.surname} ({data.email_address})
                </p>
                <form className="basic-form" onSubmit={handleSubmit}>
                  <Grid className="grid-padding-x align-justify">
                    <Cell small={12} large={6}>
                      <div className="basic-form__field">
                        <label htmlFor="forename" className="basic-form__label">
                          First name:
                        </label>

                        <input
                          className="basic-form__input"
                          type="text"
                          name="forename"
                          id="forename"
                          value={data.forename}
                          onChange={handleChange}
                          required={true}
                          placeholder="First name"
                        />
                      </div>

                      <div className="basic-form__field">
                        <label htmlFor="surname" className="basic-form__label">
                          Last name:
                        </label>

                        <input
                          className="basic-form__input"
                          type="text"
                          name="surname"
                          id="surname"
                          value={data.surname}
                          onChange={handleChange}
                          required={true}
                          placeholder="Last name"
                        />
                      </div>

                      <div className="basic-form__field">
                        <label htmlFor="email_address" className="basic-form__label">
                          Email address:
                        </label>

                        <input
                          className="basic-form__input"
                          type="text"
                          name="email_address"
                          id="email_address"
                          value={data.email_address}
                          onChange={handleChange}
                          required={true}
                          placeholder="Email address (example@email.com)"
                        />
                      </div>

                      <p>My role in the conversation</p>

                      <div className="radio-group-container">
                        <div className="basic-form__field basic-form__field--inline">
                          <input
                            className="basic-form__input"
                            type="radio"
                            name="role"
                            id="employee"
                            value="employee"
                            onChange={handleChange}
                            required={true}
                          />

                          <label htmlFor="employee" className="basic-form__label">
                            Team Member
                          </label>
                        </div>

                        <div className="basic-form__field basic-form__field--inline">
                          <input
                            className="basic-form__input"
                            type="radio"
                            name="role"
                            id="employer"
                            value="employer"
                            onChange={handleChange}
                            required={true}
                          />

                          <label htmlFor="employer" className="basic-form__label">
                            Manager
                          </label>
                        </div>
                      </div>
                    </Cell>

                    <Cell small={12} large={6}>
                      <p>You have requested to hold a conversation with {data.forename}.</p>

                      <p>
                        You'll need to specify whether your holding this conversation in the
                        capacity as a manager or as a team member. E.g. are you {data.forename}'s
                        line manager? Or are they yours?
                      </p>

                      <p>
                        Once you click 'submit' {data.forename} will receive an email with further
                        instructions on how to prepare for the conversation. You will see the
                        conversation on your dashboard page and will be invited to prepare for the
                        meeting as well.
                      </p>

                      <p>
                        Remember, you will still need to schedule the meeting, Forward Together simply
                        logs the request.
                      </p>
                    </Cell>
                  </Grid>

                  <input value={data.id} name="attendee_id" type="hidden" />

                  <div className="basic-form__actions">
                    <input
                      value="Create Conversation"
                      type="submit"
                      className="basic-form__button basic-form__button-conversation"
                      id="page-form-button--submit"
                      onChange={handleChange}

                      ref={(ref) => (basicFormButtons.current[1] = ref)}
                    />
                  </div>
                </form>
              </div>
            )}
          </Cell>
        </Grid>
      </div>
    </>
  );
};

export default ConversationsNew;
